import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import DesignServicesOutlinedIcon from "@mui/icons-material/DesignServicesOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import { jwtDecode } from "jwt-decode";
import { Trans, useTranslation } from "react-i18next";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pagePrimaryButtonSx,
  pageFieldSx,
} from "../../styles/pageModernStyles";
import { getUserById } from "../../api/User/userApi";
import { isSkillsApprover } from "./skillFichaHelpers";
import SkillAgentBrandSourcesFields, {
  buildDefaultUrlSelection,
} from "./skillAgentBrandSourcesFields";
import SkillAgentCustomSourcesFields, {
  customSourcesToUrls,
  normalizeCustomSources,
} from "./skillAgentCustomSourcesFields";
import {
  cancelSkillAgentTask,
  createSkillAgentTask,
  deleteSkillAgentTask,
  dispatchSkillAgentTask,
  fetchSkillAgentArtifactBlobUrl,
  getSkillAgentTaskById,
  getSkillAgentTasksMeta,
  listSkillAgentArtifacts,
  listSkillAgentTasks,
  markSkillAgentTaskDone,
  pushSkillAgentArtifactsToDrive,
  runSkillAgentTask,
  updateSkillAgentTask,
} from "../../api/Skills/skillAgentTasksApi";
import {
  createSkillAgentSearch,
  getSkillAgentSearchesMeta,
  listSkillAgentSearches,
} from "../../api/Skills/skillAgentSearchesApi";
import {
  createSkillAgentMoodboard,
  listSkillAgentMoodboards,
} from "../../api/Skills/skillAgentMoodboardsApi";
import { listSkillFichas } from "../../api/Skills/skillFichasApi";
import {
  buildAgentTasksTree,
  moodboardStatusLabel,
  searchStatusLabel,
  taskClientLabel,
  taskStatusColor,
  taskStatusLabel,
  taskTypeLabel,
  treeExpandIdsForSelection,
} from "./skillAgentTaskTree";
import SkillAgentTasksHelpDialog from "./SkillAgentTasksHelpDialog";
import SkillAgentSearchDetail from "./SkillAgentSearchDetail";
import SkillAgentMoodboardDetail from "./SkillAgentMoodboardDetail";

const TASK_TYPE_KEYS = [
  "presentacion",
  "propuestas",
  "siluetas",
  "mood",
  "revision",
  "otro",
];

function TreeBranch({
  node,
  depth = 0,
  expanded,
  toggle,
  selectedId,
  selectedSearchId,
  selectedMoodboardId,
  onSelectTask,
  onSelectSearch,
  onSelectMoodboard,
  onNewSearch,
  onNewMoodboard,
  onNewTask,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const open = expanded.has(node.id);
  const isSeason = node.kind === "season";
  const isSearchs = node.kind === "searchs";
  const isMoodboards = node.kind === "moodboards";
  const isDisenos = node.kind === "disenos";
  const isLibrary = isSearchs || isMoodboards || isDisenos;
  const Icon = isMoodboards
    ? DashboardCustomizeOutlinedIcon
    : isSearchs
      ? TravelExploreOutlinedIcon
      : isDisenos
        ? DesignServicesOutlinedIcon
        : isSeason
          ? CalendarMonthOutlinedIcon
          : BusinessOutlinedIcon;
  const accent = isMoodboards
    ? "#9a3412"
    : isSearchs
      ? "#0f766e"
      : isDisenos
        ? "#4338ca"
        : isSeason
          ? theme.palette.primary.main
          : "#4f46e5";

  const tasks = node.tasks || [];
  const searches = node.searches || [];
  const moodboards = node.moodboards || [];

  /** Solo hojas reales (diseños + searchs + moodboards). Nunca cuenta las carpetas. */
  const childCount = (() => {
    if (isSearchs) return searches.length;
    if (isMoodboards) return moodboards.length;
    if (isDisenos) return tasks.length;
    if (node.children?.length) {
      return node.children.reduce((sum, child) => {
        const designs = (child.disenosNode?.tasks || child.tasks || []).length;
        const sr = (child.searchsNode?.searches || child.searches || []).length;
        const mb = (child.moodboardsNode?.moodboards || child.moodboards || [])
          .length;
        return sum + designs + sr + mb;
      }, 0);
    }
    const designs = (node.disenosNode?.tasks || []).length;
    const sr = (node.searchsNode?.searches || searches).length;
    const mb = (node.moodboardsNode?.moodboards || moodboards).length;
    return designs + sr + mb;
  })();

  // Indentación suave: menos escalones = menos “ruido” horizontal
  const INDENT = 1.75;
  const branchPl = 0.75 + depth * INDENT;
  const leafPad = branchPl + 3.25;

  const leafSx = (selected, tone) => ({
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    ml: leafPad,
    mr: 0.5,
    minHeight: 32,
    px: 0.85,
    py: 0.35,
    mb: 0.15,
    borderRadius: 1.25,
    cursor: "pointer",
    border: "1px solid",
    borderColor: selected ? alpha(tone, 0.35) : "transparent",
    bgcolor: selected ? alpha(tone, 0.08) : "transparent",
    transition: "background-color 0.12s ease, border-color 0.12s ease",
    "&:hover": {
      bgcolor: alpha(tone, selected ? 0.1 : 0.04),
      "& .leaf-status": { opacity: 1 },
    },
  });

  const statusChipSx = {
    height: 18,
    maxWidth: 92,
    borderRadius: 1,
    fontWeight: 600,
    fontSize: "0.62rem",
    opacity: 0.72,
    "& .MuiChip-label": { px: 0.6 },
  };

  return (
    <Box sx={{ mb: depth === 0 ? 0.85 : 0.15 }}>
      <Box
        className="tree-branch-row"
        onClick={() => toggle(node.id)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          minHeight: isLibrary ? 28 : 34,
          px: 0.75,
          py: 0.4,
          pl: branchPl,
          mx: 0.35,
          borderRadius: 1.25,
          cursor: "pointer",
          userSelect: "none",
          borderLeft: isLibrary
            ? `2px solid ${alpha(accent, 0.45)}`
            : "2px solid transparent",
          bgcolor: "transparent",
          "&:hover": {
            bgcolor: alpha(accent, 0.05),
            "& .tree-add-btn": { opacity: 1 },
          },
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            color: "text.disabled",
            flexShrink: 0,
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.15s",
          }}
        />
        {!isLibrary ? (
          <Icon
            sx={{
              fontSize: 15,
              color: "text.disabled",
              flexShrink: 0,
              opacity: 0.7,
            }}
          />
        ) : null}
        <Typography
          variant="body2"
          fontWeight={isLibrary ? 700 : depth === 0 ? 700 : 600}
          letterSpacing={isLibrary ? 0.06 : 0}
          sx={{
            flex: 1,
            minWidth: 0,
            color: isLibrary ? accent : "text.primary",
            textTransform: isLibrary ? "uppercase" : "none",
            fontSize: isLibrary
              ? "0.68rem"
              : depth === 0
                ? "0.875rem"
                : "0.8125rem",
          }}
          noWrap
        >
          {node.label}
        </Typography>
        {isDisenos || isSearchs || isMoodboards ? (
          <IconButton
            size="small"
            className="tree-add-btn"
            aria-label={
              isDisenos
                ? t("skillAgent.tree.ariaNewDesign")
                : isSearchs
                  ? t("skillAgent.tree.ariaNewSearch")
                  : t("skillAgent.tree.ariaNewMoodboard")
            }
            onClick={(e) => {
              e.stopPropagation();
              if (isDisenos) onNewTask?.(node);
              else if (isSearchs) onNewSearch?.(node);
              else onNewMoodboard?.(node);
            }}
            sx={{
              width: 22,
              height: 22,
              color: accent,
              opacity: 0,
              flexShrink: 0,
              transition: "opacity 0.12s",
              "&:hover": { bgcolor: alpha(accent, 0.12) },
            }}
          >
            <AddOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        ) : null}
        {!isLibrary ? (
          <Typography
            variant="caption"
            sx={{
              color: "text.disabled",
              fontWeight: 600,
              fontSize: "0.68rem",
              minWidth: 16,
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            {childCount}
          </Typography>
        ) : (
          <Typography
            variant="caption"
            sx={{
              color: alpha(accent, 0.7),
              fontWeight: 600,
              fontSize: "0.65rem",
              flexShrink: 0,
            }}
          >
            {childCount}
          </Typography>
        )}
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        {node.children?.map((child) => (
          <TreeBranch
            key={child.id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            toggle={toggle}
            selectedId={selectedId}
            selectedSearchId={selectedSearchId}
            selectedMoodboardId={selectedMoodboardId}
            onSelectTask={onSelectTask}
            onSelectSearch={onSelectSearch}
            onSelectMoodboard={onSelectMoodboard}
            onNewSearch={onNewSearch}
            onNewMoodboard={onNewMoodboard}
            onNewTask={onNewTask}
          />
        ))}
        {node.disenosNode && (
          <TreeBranch
            key={node.disenosNode.id}
            node={node.disenosNode}
            depth={depth + 1}
            expanded={expanded}
            toggle={toggle}
            selectedId={selectedId}
            selectedSearchId={selectedSearchId}
            selectedMoodboardId={selectedMoodboardId}
            onSelectTask={onSelectTask}
            onSelectSearch={onSelectSearch}
            onSelectMoodboard={onSelectMoodboard}
            onNewSearch={onNewSearch}
            onNewMoodboard={onNewMoodboard}
            onNewTask={onNewTask}
          />
        )}
        {node.searchsNode && (
          <TreeBranch
            key={node.searchsNode.id}
            node={node.searchsNode}
            depth={depth + 1}
            expanded={expanded}
            toggle={toggle}
            selectedId={selectedId}
            selectedSearchId={selectedSearchId}
            selectedMoodboardId={selectedMoodboardId}
            onSelectTask={onSelectTask}
            onSelectSearch={onSelectSearch}
            onSelectMoodboard={onSelectMoodboard}
            onNewSearch={onNewSearch}
            onNewMoodboard={onNewMoodboard}
            onNewTask={onNewTask}
          />
        )}
        {node.moodboardsNode && (
          <TreeBranch
            key={node.moodboardsNode.id}
            node={node.moodboardsNode}
            depth={depth + 1}
            expanded={expanded}
            toggle={toggle}
            selectedId={selectedId}
            selectedSearchId={selectedSearchId}
            selectedMoodboardId={selectedMoodboardId}
            onSelectTask={onSelectTask}
            onSelectSearch={onSelectSearch}
            onSelectMoodboard={onSelectMoodboard}
            onNewSearch={onNewSearch}
            onNewMoodboard={onNewMoodboard}
            onNewTask={onNewTask}
          />
        )}
        {isSearchs &&
          searches.map((search) => {
            const selected = Number(selectedSearchId) === Number(search.id);
            return (
              <Box
                key={`s-${search.id}`}
                onClick={() => onSelectSearch?.(search.id)}
                sx={leafSx(selected, accent)}
              >
                <Typography
                  variant="body2"
                  fontWeight={selected ? 700 : 500}
                  noWrap
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    lineHeight: 1.25,
                    fontSize: "0.8125rem",
                  }}
                >
                  {search.Titulo}
                </Typography>
                <Chip
                  className="leaf-status"
                  size="small"
                  label={searchStatusLabel(search.Status)}
                  variant="outlined"
                  sx={statusChipSx}
                />
              </Box>
            );
          })}
        {isMoodboards &&
          moodboards.map((mb) => {
            const selected = Number(selectedMoodboardId) === Number(mb.id);
            return (
              <Box
                key={`mb-${mb.id}`}
                onClick={() => onSelectMoodboard?.(mb.id)}
                sx={leafSx(selected, accent)}
              >
                <Typography
                  variant="body2"
                  fontWeight={selected ? 700 : 500}
                  noWrap
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    lineHeight: 1.25,
                    fontSize: "0.8125rem",
                  }}
                >
                  {mb.Titulo}
                </Typography>
                <Chip
                  className="leaf-status"
                  size="small"
                  label={moodboardStatusLabel(mb.Status)}
                  variant="outlined"
                  sx={statusChipSx}
                />
              </Box>
            );
          })}
        {isDisenos &&
          tasks.map((task) => {
            const selected = Number(selectedId) === Number(task.id);
            return (
              <Box
                key={task.id}
                onClick={() => onSelectTask(task.id)}
                sx={leafSx(selected, accent)}
              >
                <Typography
                  variant="body2"
                  fontWeight={selected ? 700 : 500}
                  noWrap
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    lineHeight: 1.25,
                    fontSize: "0.8125rem",
                  }}
                >
                  {task.Titulo}
                </Typography>
                <Chip
                  className="leaf-status"
                  size="small"
                  label={taskStatusLabel(task.Status)}
                  color={taskStatusColor(task.Status)}
                  variant="outlined"
                  sx={statusChipSx}
                />
              </Box>
            );
          })}
      </Collapse>
    </Box>
  );
}

function parseArtifactMeta(artifact) {
  const raw = artifact?.MetaJson;
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function ArtifactThumb({ artifact, selected, onToggle, showResearchDebug }) {
  const isText =
    artifact.Kind === "text" || parseArtifactMeta(artifact).kind === "research";
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);

  useEffect(() => {
    if (isText) return undefined;
    let revoked = false;
    let objectUrl = "";
    (async () => {
      try {
        objectUrl = await fetchSkillAgentArtifactBlobUrl(artifact.id);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc("");
          setFailed(true);
        }
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [artifact.id, isText]);

  if (isText) {
    if (!showResearchDebug) return null;
    const meta = parseArtifactMeta(artifact);
    const brief = meta.brief || artifact.Title || "";
    const brandCount = Array.isArray(meta.brandsOk) ? meta.brandsOk.length : 0;
    return (
      <Box
        sx={{
          gridColumn: "1 / -1",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: alpha("#00838f", 0.04),
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={() => setResearchOpen((v) => !v)}
          sx={{
            px: 1.25,
            py: 0.75,
            cursor: "pointer",
            userSelect: "none",
            "&:hover": { bgcolor: alpha("#00838f", 0.06) },
          }}
        >
          <Chip
            size="small"
            color="info"
            label="Research"
            sx={{ height: 22 }}
          />
          <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1 }}>
            {artifact.Title || "Research radar"}
            {brandCount ? (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1, fontWeight: 600 }}
              >
                {brandCount} marcas
              </Typography>
            ) : null}
          </Typography>
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: "text.secondary",
              transform: researchOpen ? "rotate(180deg)" : "none",
              transition: "transform 0.15s ease",
            }}
          />
        </Stack>
        <Collapse in={researchOpen}>
          <Box sx={{ px: 1.5, pb: 1.5, pt: 0.25 }}>
            {Array.isArray(meta.brandsOk) && meta.brandsOk.length ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75 }}
              >
                {meta.brandsOk.join(" · ")}
              </Typography>
            ) : null}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.45 }}
            >
              {brief}
            </Typography>
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box
      onClick={onToggle}
      sx={{
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        bgcolor: "background.paper",
        position: "relative",
      }}
    >
      <Box
        sx={{
          aspectRatio: "3 / 4",
          bgcolor: "action.hover",
          display: "grid",
          placeItems: "center",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={artifact.Title || artifact.FileName}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : failed ? (
          <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
            Sin preview
          </Typography>
        ) : (
          <CircularProgress size={28} />
        )}
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ px: 1, py: 0.75 }}
      >
        <Checkbox size="small" checked={selected} sx={{ p: 0.25 }} />
        <Typography variant="caption" fontWeight={700} noWrap sx={{ flex: 1 }}>
          {artifact.Title || artifact.FileName}
        </Typography>
        {artifact.DriveFileId ? (
          <Chip
            size="small"
            label="Drive"
            color="success"
            sx={{ height: 20 }}
          />
        ) : null}
      </Stack>
    </Box>
  );
}

function TaskDetailPanel({
  taskId,
  fichas,
  searches = [],
  onChanged,
  onDeleted,
  showResearchDebug = false,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const noSeasonLabel = t("skillAgent.tree.noSeason");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [task, setTask] = useState(null);
  const [form, setForm] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [autoUploadDrive, setAutoUploadDrive] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [pushMsg, setPushMsg] = useState("");
  const [pushing, setPushing] = useState(false);

  const loadArtifacts = useCallback(async () => {
    if (!taskId) return;
    try {
      const data = await listSkillAgentArtifacts(taskId);
      setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
      if (data?.status && task && data.status !== task.Status) {
        const row = await getSkillAgentTaskById(taskId);
        setTask(row);
        onChanged?.(row);
      }
    } catch {
      /* ignore */
    }
  }, [taskId, task, onChanged]);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    setError("");
    setOkMsg("");
    try {
      const row = await getSkillAgentTaskById(taskId);
      setTask(row);
      setAutoUploadDrive(Boolean(row.AutoUploadDrive));
      setRunning(row.Status === "running");
      const c =
        row.ConstraintsJson && typeof row.ConstraintsJson === "object"
          ? row.ConstraintsJson
          : {};
      setForm({
        Titulo: row.Titulo || "",
        Brief: row.Brief || "",
        Temporada: row.Temporada || "",
        TipoTarea: row.TipoTarea || "otro",
        SkillFichaId: row.SkillFichaId ? String(row.SkillFichaId) : "",
        ConstraintsJson: {
          tipología: c.tipología || c.tipologia || "",
          coste: c.coste || "",
          mercado: c.mercado || "",
          refsAncla: c.refsAncla || "",
          numPropuestas:
            c.numPropuestas != null ? String(c.numPropuestas) : "6",
          searchId: c.searchId != null ? String(c.searchId) : "",
        },
      });
      try {
        const data = await listSkillAgentArtifacts(taskId);
        setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
      } catch {
        setArtifacts([]);
      }
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.taskDetail.loadError"),
      );
    } finally {
      setLoading(false);
    }
  }, [taskId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!taskId || !running) return undefined;
    const pollTimer = setInterval(async () => {
      try {
        const row = await getSkillAgentTaskById(taskId);
        setTask(row);
        onChanged?.(row);
        if (row.Status !== "running") {
          setRunning(false);
          const data = await listSkillAgentArtifacts(taskId);
          setArtifacts(Array.isArray(data?.artifacts) ? data.artifacts : []);
          if (row.Status === "needs_review") {
            setOkMsg(
              row.ResultSummary || t("skillAgent.taskDetail.proposalsReady"),
            );
          } else if (row.Status === "failed") {
            setError(
              row.ResultSummary || t("skillAgent.taskDetail.proposalsFailed"),
            );
          }
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(pollTimer);
  }, [taskId, running, onChanged, t]);

  const readOnly = task && ["running", "done"].includes(task.Status);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const constraints = {
        ...form.ConstraintsJson,
        numPropuestas: Number(form.ConstraintsJson.numPropuestas) || 6,
        searchId: form.ConstraintsJson.searchId
          ? Number(form.ConstraintsJson.searchId)
          : undefined,
      };
      if (!constraints.searchId) delete constraints.searchId;
      const updated = await updateSkillAgentTask(taskId, {
        Titulo: form.Titulo,
        Brief: form.Brief,
        Temporada: form.Temporada,
        TipoTarea: form.TipoTarea,
        SkillFichaId: form.SkillFichaId ? Number(form.SkillFichaId) : undefined,
        ConstraintsJson: constraints,
      });
      setTask(updated);
      setOkMsg(
        updated?.PromptSnapshot
          ? t("skillAgent.taskDetail.savedWithPrompt")
          : t("skillAgent.taskDetail.savedPlain"),
      );
      onChanged?.(updated);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.taskDetail.saveError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    setSaving(true);
    setError("");
    setOkMsg("");
    try {
      await handleSaveInner();
      const res = await runSkillAgentTask(taskId, {
        autoUploadDrive,
        customerFolderName: taskClientLabel(task),
      });
      setTask(res.task);
      setRunning(true);
      setSelectedIds(new Set());
      setOkMsg(res.message || t("skillAgent.taskDetail.generationStarted"));
      onChanged?.(res.task);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.taskDetail.launchError"),
      );
      setRunning(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInner = async () => {
    const constraints = {
      ...form.ConstraintsJson,
      numPropuestas: Number(form.ConstraintsJson.numPropuestas) || 6,
      searchId: form.ConstraintsJson.searchId
        ? Number(form.ConstraintsJson.searchId)
        : undefined,
    };
    if (!constraints.searchId) delete constraints.searchId;
    const updated = await updateSkillAgentTask(taskId, {
      Titulo: form.Titulo,
      Brief: form.Brief,
      Temporada: form.Temporada,
      TipoTarea: form.TipoTarea,
      SkillFichaId: form.SkillFichaId ? Number(form.SkillFichaId) : undefined,
      ConstraintsJson: constraints,
    });
    setTask(updated);
    return updated;
  };

  const handleDispatch = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await dispatchSkillAgentTask(taskId, {
        promptOnly: true,
        customerFolderName: taskClientLabel(task),
      });
      setTask(res.task);
      setOkMsg(
        res.task?.DriveFolderUrl
          ? t("skillAgent.taskDetail.driveReadyAdvanced")
          : t("skillAgent.taskDetail.driveFailed", {
              detail:
                res.driveNote ||
                res.driveStatus?.initError ||
                t("skillAgent.taskDetail.driveFailedFallback"),
            }),
      );
      onChanged?.(res.task);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.taskDetail.prepareError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePushDrive = async (all) => {
    setPushing(true);
    setSaving(true);
    setPushMsg("");
    setError("");
    setOkMsg("");
    try {
      if (!all && !selectedIds.size) {
        const pickMsg = t("skillAgent.taskDetail.needSelectProposalClick");
        setPushMsg(`error:${pickMsg}`);
        setError(pickMsg);
        return;
      }
      setPushMsg(`info:${t("skillAgent.taskDetail.pushInfo")}`);
      const body = all
        ? { all: true, customerFolderName: taskClientLabel(task) }
        : {
            artifactIds: [...selectedIds],
            customerFolderName: taskClientLabel(task),
          };
      const res = await pushSkillAgentArtifactsToDrive(taskId, body);
      setArtifacts(Array.isArray(res.artifacts) ? res.artifacts : artifacts);
      if (res.task) {
        setTask(res.task);
        onChanged?.(res.task);
      }
      const n = res.uploaded?.length || 0;
      const msg = all
        ? t("skillAgent.taskDetail.uploadedProposals", { n })
        : t("skillAgent.taskDetail.uploadedSelectedProposals", { n });
      setOkMsg(msg);
      setPushMsg(`ok:${msg}`);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("skillAgent.taskDetail.uploadDriveError");
      setError(msg);
      setPushMsg(`error:${msg}`);
    } finally {
      setPushing(false);
      setSaving(false);
    }
  };

  const handleDone = async () => {
    setSaving(true);
    try {
      const updated = await markSkillAgentTaskDone(taskId);
      setTask(updated);
      setOkMsg(t("skillAgent.taskDetail.markedDone"));
      onChanged?.(updated);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.taskDetail.errorGeneric"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t("skillAgent.taskDetail.confirmCancel"))) return;
    setSaving(true);
    try {
      const updated = await cancelSkillAgentTask(taskId);
      setTask(updated);
      setRunning(false);
      onChanged?.(updated);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("skillAgent.taskDetail.confirmDelete"))) return;
    try {
      await deleteSkillAgentTask(taskId);
      onDeleted?.(taskId);
    } catch {
      setError(t("skillAgent.taskDetail.deleteError"));
    }
  };

  const copyPrompt = async () => {
    if (!task?.PromptSnapshot) return;
    try {
      await navigator.clipboard.writeText(task.PromptSnapshot);
      setOkMsg(t("skillAgent.taskDetail.promptCopiedShort"));
    } catch {
      setError(t("skillAgent.taskDetail.copyFailed"));
    }
  };

  if (loading || !form) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const canLaunch =
    task.Status !== "done" &&
    task.Status !== "cancelled" &&
    task.Status !== "running";

  const imageArtifacts = artifacts.filter(
    (a) => a.Kind !== "text" && parseArtifactMeta(a).kind !== "research",
  );

  return (
    <Stack spacing={2} sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ sm: "flex-start" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={700}
          >
            {t("skillAgent.taskDetail.orderLabel", { id: task.id })}
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.25 }}>
            {task.Titulo}
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ mt: 1 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              size="small"
              icon={<CalendarMonthOutlinedIcon />}
              label={task.Temporada || noSeasonLabel}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<BusinessOutlinedIcon />}
              label={taskClientLabel(task)}
              variant="outlined"
            />
            <Chip
              size="small"
              label={taskStatusLabel(task.Status)}
              color={taskStatusColor(task.Status)}
            />
          </Stack>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t("skillAgent.taskDetail.deleteTooltip")}>
            <IconButton color="error" onClick={handleDelete} size="small">
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {okMsg && (
        <Alert severity="success" onClose={() => setOkMsg("")}>
          {okMsg}
        </Alert>
      )}
      {task.ResultSummary && task.Status === "failed" && (
        <Alert severity="error">{task.ResultSummary}</Alert>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.paper, 0.85),
        }}
      >
        <Stack spacing={1.5}>
          <TextField
            label={t("skillAgent.form.title")}
            size="small"
            value={form.Titulo}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, Titulo: e.target.value }))}
            sx={pageFieldSx}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <FormControl
              size="small"
              sx={{ flex: 1, ...pageFieldSx }}
              disabled={readOnly}
            >
              <InputLabel>{t("skillAgent.form.clientFicha")}</InputLabel>
              <Select
                label={t("skillAgent.form.clientFicha")}
                value={form.SkillFichaId}
                onChange={(e) =>
                  setForm((p) => ({ ...p, SkillFichaId: e.target.value }))
                }
              >
                <MenuItem value="">—</MenuItem>
                {fichas.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.Titulo || f.SkillSlug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 140, ...pageFieldSx }}
              disabled={readOnly}
            >
              <InputLabel>{t("skillAgent.form.taskType")}</InputLabel>
              <Select
                label={t("skillAgent.form.taskType")}
                value={form.TipoTarea}
                onChange={(e) =>
                  setForm((p) => ({ ...p, TipoTarea: e.target.value }))
                }
              >
                {TASK_TYPE_KEYS.map((k) => (
                  <MenuItem key={k} value={k}>
                    {taskTypeLabel(k)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t("skillAgent.form.season")}
              size="small"
              value={form.Temporada}
              disabled={readOnly}
              onChange={(e) =>
                setForm((p) => ({ ...p, Temporada: e.target.value }))
              }
              sx={{ minWidth: 110, ...pageFieldSx }}
            />
          </Stack>
          <TextField
            label={t("skillAgent.form.brief")}
            multiline
            minRows={3}
            value={form.Brief}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, Brief: e.target.value }))}
            sx={pageFieldSx}
          />
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            {t("skillAgent.form.constraintsHeading")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            {["tipología", "coste", "mercado", "refsAncla"].map((key) => (
              <TextField
                key={key}
                label={key}
                size="small"
                value={form.ConstraintsJson[key] || ""}
                disabled={readOnly}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    ConstraintsJson: {
                      ...p.ConstraintsJson,
                      [key]: e.target.value,
                    },
                  }))
                }
                sx={{ flex: 1, ...pageFieldSx }}
              />
            ))}
            <TextField
              label={t("skillAgent.form.numProposals")}
              size="small"
              type="number"
              inputProps={{ min: 1, max: 8 }}
              value={form.ConstraintsJson.numPropuestas || "6"}
              disabled={readOnly}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  ConstraintsJson: {
                    ...p.ConstraintsJson,
                    numPropuestas: e.target.value,
                  },
                }))
              }
              sx={{ width: 120, ...pageFieldSx }}
            />
            <FormControl size="small" sx={{ minWidth: 200, ...pageFieldSx }}>
              <InputLabel>{t("skillAgent.form.useSearch")}</InputLabel>
              <Select
                label={t("skillAgent.form.useSearch")}
                value={form.ConstraintsJson.searchId || ""}
                disabled={readOnly}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    ConstraintsJson: {
                      ...p.ConstraintsJson,
                      searchId: e.target.value,
                    },
                  }))
                }
              >
                <MenuItem value="">{t("skillAgent.form.radarOnRun")}</MenuItem>
                {(searches || [])
                  .filter((s) => s.Status === "done")
                  .map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.Titulo}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={autoUploadDrive}
                disabled={readOnly}
                onChange={(e) => setAutoUploadDrive(e.target.checked)}
              />
            }
            label={t("skillAgent.taskDetail.autoUploadDrive")}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {!readOnly && (
              <Button
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                disabled={saving}
                onClick={handleSave}
              >
                {t("skillAgent.common.save")}
              </Button>
            )}
            {canLaunch && (
              <Button
                variant="contained"
                startIcon={
                  saving || running ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PlayArrowOutlinedIcon />
                  )
                }
                sx={pagePrimaryButtonSx}
                disabled={saving || running}
                onClick={handleRun}
              >
                {t("skillAgent.taskDetail.launchProposals")}
              </Button>
            )}
            {task.Status === "running" && (
              <Chip
                icon={<CircularProgress size={14} />}
                label={t("skillAgent.taskDetail.generatingChip")}
                color="info"
              />
            )}
            {task.Status !== "done" && task.Status !== "cancelled" && (
              <Button
                color="success"
                variant="contained"
                startIcon={<CheckCircleOutlineIcon />}
                disabled={saving || task.Status === "running"}
                onClick={handleDone}
              >
                {t("skillAgent.taskDetail.doneButton")}
              </Button>
            )}
            {task.Status !== "cancelled" && task.Status !== "done" && (
              <Button color="inherit" disabled={saving} onClick={handleCancel}>
                {t("skillAgent.common.cancel")}
              </Button>
            )}
            {task.Status === "done" || task.Status === "cancelled" ? (
              <Button
                variant="outlined"
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    const updated = await updateSkillAgentTask(taskId, {
                      Status: "draft",
                    });
                    setTask(updated);
                    setRunning(false);
                    setOkMsg(t("skillAgent.taskDetail.reopenSuccess"));
                    onChanged?.(updated);
                  } catch (err) {
                    setError(
                      err?.response?.data?.error ||
                        t("skillAgent.taskDetail.reopenError"),
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {t("skillAgent.taskDetail.reopen")}
              </Button>
            ) : null}
            {task.DriveFolderUrl && (
              <Button
                startIcon={<OpenInNewOutlinedIcon />}
                href={task.DriveFolderUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("skillAgent.taskDetail.openDriveFolder")}
              </Button>
            )}
            <Button
              size="small"
              variant="text"
              disabled={saving || task.Status === "running"}
              onClick={handleDispatch}
            >
              {t("skillAgent.taskDetail.prepareFolderOnly")}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: alpha("#1b5e20", 0.03),
          borderColor: alpha("#1b5e20", 0.14),
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={800}>
              {t("skillAgent.taskDetail.resultsTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {imageArtifacts.length
                ? t("skillAgent.taskDetail.resultsHintSelected", {
                    n: imageArtifacts.length,
                  })
                : t("skillAgent.taskDetail.resultsHintEmpty")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                pushing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CloudUploadOutlinedIcon />
                )
              }
              disabled={pushing || saving || !imageArtifacts.length}
              onClick={() => handlePushDrive(false)}
            >
              {t("skillAgent.taskDetail.saveSelectedLabel")}
              {selectedIds.size ? ` (${selectedIds.size})` : ""}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={
                pushing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CloudUploadOutlinedIcon />
                )
              }
              disabled={pushing || saving || !imageArtifacts.length}
              onClick={() => handlePushDrive(true)}
            >
              {t("skillAgent.taskDetail.saveAll")}
            </Button>
            <Button
              size="small"
              onClick={loadArtifacts}
              disabled={saving || pushing}
            >
              {t("skillAgent.taskDetail.refresh")}
            </Button>
          </Stack>
        </Stack>

        {pushMsg ? (
          <Alert
            severity={
              pushMsg.startsWith("ok:")
                ? "success"
                : pushMsg.startsWith("info:")
                  ? "info"
                  : "error"
            }
            sx={{ mb: 1.5 }}
            onClose={() => setPushMsg("")}
          >
            {pushMsg.replace(/^(ok|error|info):/, "")}
          </Alert>
        ) : null}

        {task.Status === "running" && !imageArtifacts.length ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t("skillAgent.taskDetail.generatingPlaceholder")}
            </Typography>
          </Box>
        ) : imageArtifacts.length ||
          (showResearchDebug && artifacts.some((a) => a.Kind === "text")) ? (
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: "repeat(3, 1fr)",
                md: "repeat(4, 1fr)",
              },
            }}
          >
            {artifacts.map((a) => (
              <ArtifactThumb
                key={a.id}
                artifact={a}
                selected={selectedIds.has(a.id)}
                onToggle={() => toggleSelect(a.id)}
                showResearchDebug={showResearchDebug}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" component="span">
            <Trans
              i18nKey="skillAgent.taskDetail.noProposalsHtml"
              components={{ b: <b /> }}
            />
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box
          onClick={() => setPromptOpen((v) => !v)}
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            bgcolor: alpha("#0d47a1", 0.04),
            "&:hover": { bgcolor: alpha("#0d47a1", 0.08) },
          }}
        >
          <ExpandMoreIcon
            sx={{
              transform: promptOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.15s",
              fontSize: 20,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {t("skillAgent.taskDetail.technicalSection")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("skillAgent.taskDetail.promptHeading")}
              {task.SkillVersionUsed != null
                ? ` · skill v${task.SkillVersionUsed}`
                : ""}
              {task.DispatchMode ? ` · ${task.DispatchMode}` : ""}
            </Typography>
          </Box>
          {task.PromptSnapshot && (
            <Button
              size="small"
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={(e) => {
                e.stopPropagation();
                copyPrompt();
              }}
            >
              {t("skillAgent.common.copy")}
            </Button>
          )}
        </Box>
        <Collapse in={promptOpen}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: alpha("#000", 0.03),
              fontSize: "0.75rem",
              whiteSpace: "pre-wrap",
              maxHeight: 280,
              overflow: "auto",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            {task.PromptSnapshot || t("skillAgent.taskDetail.promptEmpty")}
          </Box>
        </Collapse>
      </Paper>
    </Stack>
  );
}

export default function SkillAgentTasksPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { contentMargin } = useContext(SidebarContext);

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [sessionUser, setSessionUser] = useState(null);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [searches, setSearches] = useState([]);
  const [moodboards, setMoodboards] = useState([]);
  const [searchBrands, setSearchBrands] = useState([]);
  const [fichas, setFichas] = useState([]);
  const [meta, setMeta] = useState(null);
  const [groupBy, setGroupBy] = useState("client");
  const [treeCollapsed, setTreeCollapsed] = useState(() => {
    try {
      return localStorage.getItem("skillAgentTreeCollapsed") === "1";
    } catch {
      return false;
    }
  });
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedId, setSelectedId] = useState(
    () => searchParams.get("id") || "",
  );
  const [selectedSearchId, setSelectedSearchId] = useState(
    () => searchParams.get("search") || "",
  );
  const [selectedMoodboardId, setSelectedMoodboardId] = useState(
    () => searchParams.get("moodboard") || "",
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [createSearchOpen, setCreateSearchOpen] = useState(false);
  const [createMoodboardOpen, setCreateMoodboardOpen] = useState(false);
  const [searchContext, setSearchContext] = useState(null);
  const [moodboardContext, setMoodboardContext] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingSearch, setCreatingSearch] = useState(false);
  const [creatingMoodboard, setCreatingMoodboard] = useState(false);
  const [moodboardFormError, setMoodboardFormError] = useState("");
  const [newTask, setNewTask] = useState({
    SkillFichaId: "",
    Titulo: "",
    Temporada: "",
    TipoTarea: "propuestas",
    Brief: "",
    searchId: "",
  });
  const [newSearch, setNewSearch] = useState({
    Titulo: "",
    Temporada: "",
    Tipologia: "chaqueta Urban",
    CustomerId: "",
    CustomerName: "",
    brandIds: [],
    urlSelection: {},
    customSources: [],
    maxImagesPerBrand: "3",
  });
  const [newMoodboard, setNewMoodboard] = useState({
    Titulo: "",
    CategoryLabel: "Aviator - JKT",
    Temporada: "",
    CustomerId: "",
    CustomerName: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }
      const decoded = jwtDecode(token);
      const user = await getUserById(decoded.userId);
      setSessionUser(user || null);
      if (!user?.Permiso_TareasAgentes) {
        setForbidden(true);
        return;
      }
      const [
        tasks,
        fichasData,
        metaData,
        searchesData,
        searchesMeta,
        moodboardsData,
      ] = await Promise.all([
        listSkillAgentTasks({}),
        listSkillFichas({}),
        getSkillAgentTasksMeta(),
        listSkillAgentSearches({}).catch(() => []),
        getSkillAgentSearchesMeta().catch(() => null),
        listSkillAgentMoodboards({}).catch(() => []),
      ]);
      const list = Array.isArray(tasks) ? tasks : [];
      const searchList = Array.isArray(searchesData) ? searchesData : [];
      const moodboardList = Array.isArray(moodboardsData) ? moodboardsData : [];
      setRows(list);
      setSearches(searchList);
      setMoodboards(moodboardList);
      setFichas(Array.isArray(fichasData) ? fichasData : []);
      setMeta(metaData || null);
      setSearchBrands(
        (Array.isArray(searchesMeta?.brands) ? searchesMeta.brands : [])
          .slice()
          .sort((a, b) =>
            String(a.name || a.id).localeCompare(String(b.name || b.id), "es", {
              sensitivity: "base",
            }),
          ),
      );
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) setForbidden(true);
      else setError(t("skillAgent.page.loadTasksError"));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Sync selection from URL
  useEffect(() => {
    if (loading || forbidden) return;
    const urlId = searchParams.get("id");
    const urlSearch = searchParams.get("search");
    const urlMoodboard = searchParams.get("moodboard");
    if (urlMoodboard) {
      if (String(selectedMoodboardId) !== String(urlMoodboard)) {
        setSelectedMoodboardId(String(urlMoodboard));
        setSelectedSearchId("");
        setSelectedId("");
      }
      return;
    }
    if (urlSearch) {
      if (String(selectedSearchId) !== String(urlSearch)) {
        setSelectedSearchId(String(urlSearch));
        setSelectedMoodboardId("");
        setSelectedId("");
      }
      return;
    }
    if (urlId) {
      if (String(selectedId) !== String(urlId)) {
        setSelectedId(String(urlId));
        setSelectedSearchId("");
        setSelectedMoodboardId("");
      }
      return;
    }
    if (
      !selectedId &&
      !selectedSearchId &&
      !selectedMoodboardId &&
      rows.some((t) => Number(t.id) === 2)
    ) {
      setSelectedId("2");
      setSearchParams({ id: "2" }, { replace: true });
    }
  }, [
    loading,
    forbidden,
    rows,
    searchParams,
    selectedId,
    selectedSearchId,
    selectedMoodboardId,
    setSearchParams,
  ]);

  // Expandir camino del ítem seleccionado (p. ej. tras F5 con ?moodboard=)
  useEffect(() => {
    if (loading || forbidden) return;
    if (!selectedMoodboardId && !selectedSearchId && !selectedId) return;
    const treeNow = buildAgentTasksTree(rows, groupBy, searches, moodboards);
    const path = treeExpandIdsForSelection(treeNow, {
      moodboardId: selectedMoodboardId,
      searchId: selectedSearchId,
      taskId: selectedId,
    });
    if (!path.length) return;
    setExpanded((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of path) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [
    loading,
    forbidden,
    selectedMoodboardId,
    selectedSearchId,
    selectedId,
    groupBy,
    rows,
    searches,
    moodboards,
  ]);

  const filteredRows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter((r) => {
      const blob = [r.Titulo, r.Brief, r.SkillSlug, r.Temporada, r.CustomerName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(qq);
    });
  }, [rows, q]);

  const filteredSearches = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return searches;
    return searches.filter((r) => {
      const blob = [r.Titulo, r.Brief, r.Temporada, r.CustomerName, r.Tipologia]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(qq);
    });
  }, [searches, q]);

  const filteredMoodboards = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return moodboards;
    return moodboards.filter((r) => {
      const blob = [r.Titulo, r.CategoryLabel, r.Temporada, r.CustomerName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(qq);
    });
  }, [moodboards, q]);

  const tree = useMemo(
    () =>
      buildAgentTasksTree(
        filteredRows,
        groupBy,
        filteredSearches,
        filteredMoodboards,
      ),
    [
      filteredRows,
      groupBy,
      filteredSearches,
      filteredMoodboards,
      i18n.language,
    ],
  );

  const searchesForNewTask = useMemo(() => {
    const ficha = fichas.find(
      (f) => String(f.id) === String(newTask.SkillFichaId),
    );
    const customerId = ficha?.CustomerId;
    const temp = String(newTask.Temporada || "").trim();
    return searches.filter((s) => {
      if (s.Status !== "done") return false;
      if (
        customerId &&
        s.CustomerId &&
        Number(s.CustomerId) !== Number(customerId)
      ) {
        return false;
      }
      if (temp && String(s.Temporada || "").trim() !== temp) return false;
      return true;
    });
  }, [searches, fichas, newTask.SkillFichaId, newTask.Temporada]);

  const toggleNode = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectTask = (id) => {
    setSelectedId(String(id));
    setSelectedSearchId("");
    setSelectedMoodboardId("");
    setSearchParams({ id: String(id) });
  };

  const handleSelectSearch = (id) => {
    setSelectedSearchId(String(id));
    setSelectedMoodboardId("");
    setSelectedId("");
    setSearchParams({ search: String(id) });
  };

  const handleSelectMoodboard = (id) => {
    setSelectedMoodboardId(String(id));
    setSelectedSearchId("");
    setSelectedId("");
    setSearchParams({ moodboard: String(id) });
  };

  const openNewTaskDialog = (ctxNode) => {
    const rawSeason = String(ctxNode?.Temporada || "").trim();
    const season =
      rawSeason && rawSeason !== t("skillAgent.tree.noSeason") ? rawSeason : "";
    const clientName = String(
      ctxNode?.CustomerName || ctxNode?.clientLabel || "",
    ).trim();
    let fichaId = "";
    if (ctxNode?.CustomerId) {
      const match = fichas.find(
        (f) => Number(f.CustomerId) === Number(ctxNode.CustomerId),
      );
      if (match) fichaId = String(match.id);
    }
    if (!fichaId && clientName) {
      const match = fichas.find(
        (f) =>
          f.Titulo === clientName ||
          f.SkillSlug === clientName ||
          (f.Titulo && clientName.includes(String(f.Titulo))) ||
          (f.SkillSlug && clientName.includes(String(f.SkillSlug))),
      );
      if (match) fichaId = String(match.id);
    }
    setNewTask({
      SkillFichaId: fichaId,
      Titulo: "",
      Temporada: season,
      TipoTarea: "propuestas",
      Brief: "",
      searchId: "",
    });
    setCreateOpen(true);
  };

  const openNewSearchDialog = async (ctxNode) => {
    const rawSeason = String(ctxNode?.Temporada || "").trim();
    const season =
      rawSeason && rawSeason !== t("skillAgent.tree.noSeason") ? rawSeason : "";
    const clientName = String(
      ctxNode?.CustomerName || ctxNode?.clientLabel || "",
    ).trim();
    let customerId = ctxNode?.CustomerId ? String(ctxNode.CustomerId) : "";
    if (!customerId && clientName) {
      const match = fichas.find(
        (f) =>
          f.Titulo === clientName ||
          f.SkillSlug === clientName ||
          (f.Titulo && clientName.includes(String(f.Titulo))) ||
          (f.SkillSlug && clientName.includes(String(f.SkillSlug))),
      );
      if (match?.CustomerId) customerId = String(match.CustomerId);
    }
    setSearchContext({
      ...(ctxNode || {}),
      CustomerName: clientName || null,
      Temporada: season || null,
      CustomerId: customerId ? Number(customerId) : null,
    });

    let brandsForClient = searchBrands;
    if (customerId) {
      try {
        const meta = await getSkillAgentSearchesMeta({
          customerId: Number(customerId),
        });
        brandsForClient = (Array.isArray(meta?.brands) ? meta.brands : [])
          .slice()
          .sort((a, b) =>
            String(a.name || a.id).localeCompare(String(b.name || b.id), "es", {
              sensitivity: "base",
            }),
          );
        setSearchBrands(brandsForClient);
      } catch {
        /* keep previous */
      }
    }

    setNewSearch({
      Titulo: "",
      Temporada: season,
      Tipologia: "chaqueta Urban",
      CustomerId: customerId,
      CustomerName: clientName,
      brandIds: brandsForClient.map((b) => b.id),
      urlSelection: buildDefaultUrlSelection(
        brandsForClient,
        brandsForClient.map((b) => b.id),
      ),
      customSources: [],
      maxImagesPerBrand: "3",
    });
    setCreateSearchOpen(true);
  };

  const handleCreateSearch = async () => {
    if (!String(newSearch.Titulo || "").trim()) return;
    if (!newSearch.CustomerId && !newSearch.CustomerName) {
      setError(t("skillAgent.create.searchNeedClient"));
      return;
    }
    setCreatingSearch(true);
    setError("");
    try {
      const tipology = String(newSearch.Tipologia || "").trim();
      if (!tipology) {
        setError(t("skillAgent.create.tipologyRequired"));
        setCreatingSearch(false);
        return;
      }
      const customSources = normalizeCustomSources(newSearch.customSources);
      const customUrls = customSourcesToUrls(customSources);
      const researchBrands = Array.isArray(newSearch.brandIds)
        ? newSearch.brandIds
        : [];
      if (
        !researchBrands.length &&
        !customSources.filter((s) => s.enabled).length
      ) {
        setError(t("skillAgent.create.needBrandOrUrl"));
        setCreatingSearch(false);
        return;
      }
      const created = await createSkillAgentSearch({
        Titulo: newSearch.Titulo.trim(),
        Temporada: newSearch.Temporada || null,
        Tipologia: tipology,
        CustomerId: newSearch.CustomerId ? Number(newSearch.CustomerId) : null,
        ConstraintsJson: {
          tipología: tipology,
          researchBrands,
          brandUrlSelection: newSearch.urlSelection || {},
          customSources,
          customUrls,
          maxImagesPerBrand: Number(newSearch.maxImagesPerBrand) || 3,
          customerName: newSearch.CustomerName || null,
          preferNewest: true,
          maxAgeDays: 60,
          skipKnown: true,
          skipKnownScope: "clienteTemporada",
        },
      });
      setCreateSearchOpen(false);
      await load();
      if (created?.id) handleSelectSearch(created.id);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.create.searchError"),
      );
    } finally {
      setCreatingSearch(false);
    }
  };

  const openNewMoodboardDialog = (ctxNode) => {
    const rawSeason = String(ctxNode?.Temporada || "").trim();
    const season =
      rawSeason && rawSeason !== t("skillAgent.tree.noSeason") ? rawSeason : "";
    const clientName = String(
      ctxNode?.CustomerName || ctxNode?.clientLabel || "",
    ).trim();
    let customerId = ctxNode?.CustomerId ? String(ctxNode.CustomerId) : "";
    if (!customerId && clientName) {
      const match = fichas.find(
        (f) =>
          f.Titulo === clientName ||
          f.SkillSlug === clientName ||
          (f.Titulo && clientName.includes(String(f.Titulo))) ||
          (f.SkillSlug && clientName.includes(String(f.SkillSlug))),
      );
      if (match?.CustomerId) customerId = String(match.CustomerId);
    }
    setMoodboardContext({
      CustomerName: clientName || null,
      Temporada: season || null,
      CustomerId: customerId ? Number(customerId) : null,
    });
    setMoodboardFormError("");
    setNewMoodboard({
      Titulo: "Aviator - JKT",
      CategoryLabel: "Aviator - JKT",
      Temporada: season,
      CustomerId: customerId,
      CustomerName: clientName,
    });
    setCreateMoodboardOpen(true);
  };

  const handleCreateMoodboard = async () => {
    const titulo =
      String(newMoodboard.Titulo || "").trim() ||
      String(newMoodboard.CategoryLabel || "").trim();
    if (!titulo) {
      setMoodboardFormError(t("skillAgent.create.moodboardTitleRequired"));
      return;
    }
    if (!newMoodboard.CustomerId && !newMoodboard.CustomerName) {
      setMoodboardFormError(t("skillAgent.create.moodboardNeedClient"));
      return;
    }
    setCreatingMoodboard(true);
    setMoodboardFormError("");
    setError("");
    try {
      const created = await createSkillAgentMoodboard({
        Titulo: titulo,
        CategoryLabel:
          String(newMoodboard.CategoryLabel || "").trim() || titulo,
        Temporada: newMoodboard.Temporada || null,
        CustomerId: newMoodboard.CustomerId
          ? Number(newMoodboard.CustomerId)
          : null,
      });
      setCreateMoodboardOpen(false);
      await load();
      if (created?.id) handleSelectMoodboard(created.id);
    } catch (err) {
      const msg =
        err?.response?.data?.error || t("skillAgent.create.moodboardError");
      setMoodboardFormError(msg);
      setError(msg);
    } finally {
      setCreatingMoodboard(false);
    }
  };

  const handleCreate = async () => {
    if (!newTask.Titulo.trim()) {
      setError(t("skillAgent.create.taskTitleRequired"));
      return;
    }
    setCreating(true);
    setError("");
    try {
      const created = await createSkillAgentTask({
        SkillFichaId: newTask.SkillFichaId
          ? Number(newTask.SkillFichaId)
          : null,
        Titulo: newTask.Titulo.trim(),
        Temporada: newTask.Temporada.trim() || null,
        TipoTarea: newTask.TipoTarea,
        Brief: newTask.Brief,
        ConstraintsJson: newTask.searchId
          ? {
              searchId: Number(newTask.searchId),
              tipología: "",
              numPropuestas: 6,
            }
          : { numPropuestas: 6 },
      });
      setCreateOpen(false);
      setNewTask({
        SkillFichaId: "",
        Titulo: "",
        Temporada: "",
        TipoTarea: "propuestas",
        Brief: "",
        searchId: "",
      });
      await load();
      if (created?.id) handleSelectTask(created.id);
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.create.taskError"));
    } finally {
      setCreating(false);
    }
  };

  if (forbidden) {
    return (
      <Box sx={{ ...contentMargin, p: 3 }}>
        <Alert severity="warning">{t("skillAgent.page.forbidden")}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        height: "100dvh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: alpha(theme.palette.primary.main, 0.025),
        backgroundImage: `radial-gradient(1000px 400px at 0% -20%, ${alpha(theme.palette.primary.main, 0.09)}, transparent)`,
        ...contentMargin,
      }}
    >
      {/* Header — se oculta con moodboard abierto (cabecera del board arriba) */}
      {!selectedMoodboardId ? (
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 1.5, md: 2.5 },
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.background.paper, 0.92),
            backdropFilter: "blur(10px)",
            zIndex: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                }}
              >
                <FolderOpenOutlinedIcon fontSize="small" />
              </Box>
              <Typography variant="h6" fontWeight={800}>
                {t("skillAgent.page.title")}
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<HelpOutlineOutlinedIcon />}
              onClick={() => setHelpOpen(true)}
              size="small"
              sx={{
                height: 34,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
              }}
            >
              {t("skillAgent.page.instructions")}
            </Button>
          </Stack>
        </Box>
      ) : null}

      {error && (
        <Alert
          severity="error"
          sx={{ mx: 2, mt: 1.5, flexShrink: 0 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {/* Body: tree + detail */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: treeCollapsed
              ? "44px minmax(0, 1fr)"
              : "minmax(260px, 320px) minmax(0, 1fr)",
          },
          gridTemplateRows: {
            xs: treeCollapsed ? "48px minmax(0, 1fr)" : "auto minmax(0, 1fr)",
            md: "minmax(0, 1fr)",
          },
          gap: 0,
        }}
      >
        {/* Tree panel */}
        <Paper
          square
          elevation={0}
          sx={{
            borderRight: { md: "1px solid" },
            borderBottom: { xs: "1px solid", md: "none" },
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            minHeight: { xs: treeCollapsed ? 0 : 280, md: 0 },
            maxHeight: {
              xs: treeCollapsed ? 48 : "42dvh",
              md: "none",
            },
            bgcolor: alpha(theme.palette.background.paper, 0.7),
            overflow: "hidden",
          }}
        >
          {treeCollapsed ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: { xs: "row", md: "column" },
                alignItems: "center",
                justifyContent: { xs: "flex-start", md: "flex-start" },
                gap: 1,
                py: { xs: 0.5, md: 1.25 },
                px: { xs: 1, md: 0.5 },
              }}
            >
              <Tooltip title={t("skillAgent.tree.showTree")} placement="right">
                <IconButton
                  size="small"
                  onClick={() => {
                    setTreeCollapsed(false);
                    try {
                      localStorage.setItem("skillAgentTreeCollapsed", "0");
                    } catch {
                      /* ignore */
                    }
                  }}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <ViewSidebarOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: "text.disabled",
                  display: { xs: "none", md: "block" },
                }}
              />
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{
                  display: { xs: "none", md: "block" },
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                  fontWeight: 700,
                  letterSpacing: 0.06,
                  mt: 1,
                }}
              >
                {t("skillAgent.tree.collapsedLabel")}
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1.25 }}
                >
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={groupBy}
                    onChange={(_, v) => v && setGroupBy(v)}
                    sx={{
                      flex: 1,
                      gap: 1,
                      height: 34,
                      "& .MuiToggleButtonGroup-grouped": {
                        border: "1px solid !important",
                        borderColor: `${theme.palette.divider} !important`,
                        borderRadius: "8px !important",
                        marginLeft: "0 !important",
                        textTransform: "none",
                        fontWeight: 700,
                        py: 0,
                        height: 34,
                        flex: 1,
                      },
                      "& .MuiToggleButtonGroup-grouped.Mui-selected": {
                        borderColor: `${alpha(theme.palette.primary.main, 0.45)} !important`,
                      },
                    }}
                  >
                    <ToggleButton value="season">
                      {t("skillAgent.tree.groupBySeason")}
                    </ToggleButton>
                    <ToggleButton value="client">
                      {t("skillAgent.tree.groupByClient")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Tooltip title={t("skillAgent.tree.collapseTree")}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setTreeCollapsed(true);
                        try {
                          localStorage.setItem("skillAgentTreeCollapsed", "1");
                        } catch {
                          /* ignore */
                        }
                      }}
                      sx={{
                        flexShrink: 0,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.5,
                        width: 34,
                        height: 34,
                      }}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    placeholder={t("skillAgent.tree.searchPlaceholder")}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      flex: 1,
                      ...pageFieldSx,
                      "& .MuiOutlinedInput-root": {
                        height: 34,
                        borderRadius: 2,
                      },
                    }}
                  />
                </Stack>
              </Box>

              <Box sx={{ flex: 1, overflow: "auto", p: 0.75 }}>
                {loading ? (
                  <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : tree.length === 0 ? (
                  <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
                    <FolderOpenOutlinedIcon
                      sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                    />
                    <Typography fontWeight={700} color="text.secondary">
                      {t("skillAgent.tree.emptyTitle")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {t("skillAgent.tree.emptyHint")}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddOutlinedIcon />}
                      sx={pagePrimaryButtonSx}
                      onClick={() => setCreateOpen(true)}
                    >
                      {t("skillAgent.tree.newTaskButton")}
                    </Button>
                  </Box>
                ) : (
                  tree.map((node) => (
                    <TreeBranch
                      key={node.id}
                      node={node}
                      depth={0}
                      expanded={expanded}
                      toggle={toggleNode}
                      selectedId={selectedId}
                      selectedSearchId={selectedSearchId}
                      selectedMoodboardId={selectedMoodboardId}
                      onSelectTask={handleSelectTask}
                      onSelectSearch={handleSelectSearch}
                      onSelectMoodboard={handleSelectMoodboard}
                      onNewSearch={openNewSearchDialog}
                      onNewMoodboard={openNewMoodboardDialog}
                      onNewTask={openNewTaskDialog}
                    />
                  ))
                )}
              </Box>
            </>
          )}
        </Paper>

        {/* Detail panel */}
        <Box
          sx={{
            minHeight: 0,
            minWidth: 0,
            maxWidth: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: selectedMoodboardId ? "hidden" : "auto",
            bgcolor: alpha(theme.palette.background.default, 0.35),
          }}
        >
          {selectedMoodboardId ? (
            <SkillAgentMoodboardDetail
              key={selectedMoodboardId}
              moodboardId={selectedMoodboardId}
              viewerName={
                sessionUser?.username ||
                sessionUser?.Nombre ||
                sessionUser?.name ||
                ""
              }
              viewerUserId={sessionUser?.id || null}
              onOpenHelp={() => setHelpOpen(true)}
              onChanged={() => load()}
              onDeleted={() => {
                setSelectedMoodboardId("");
                setSearchParams({});
                load();
              }}
            />
          ) : selectedSearchId ? (
            <SkillAgentSearchDetail
              key={selectedSearchId}
              searchId={selectedSearchId}
              onChanged={() => load()}
              onDeleted={() => {
                setSelectedSearchId("");
                setSearchParams({});
                load();
              }}
            />
          ) : !selectedId ? (
            <Box
              sx={{
                height: "100%",
                minHeight: 320,
                display: "grid",
                placeItems: "center",
                p: 4,
                textAlign: "center",
              }}
            >
              <Box sx={{ maxWidth: 420 }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 4,
                    mx: "auto",
                    mb: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: "primary.main",
                  }}
                >
                  <AssignmentOutlinedIcon sx={{ fontSize: 36 }} />
                </Box>
                <Typography variant="h6" fontWeight={800}>
                  {t("skillAgent.page.pickItemTitle")}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ mt: 1 }}
                  component="div"
                >
                  <Trans
                    i18nKey="skillAgent.page.pickItemBody"
                    components={{ b: <b /> }}
                  />
                </Typography>
              </Box>
            </Box>
          ) : (
            <TaskDetailPanel
              key={`${selectedId}-${
                rows.find((r) => Number(r.id) === Number(selectedId))?.Status ||
                ""
              }`}
              taskId={selectedId}
              fichas={fichas}
              searches={searches}
              showResearchDebug={isSkillsApprover(sessionUser)}
              onChanged={(updated) => {
                setRows((prev) =>
                  prev.map((r) =>
                    Number(r.id) === Number(updated.id)
                      ? { ...r, ...updated }
                      : r,
                  ),
                );
              }}
              onDeleted={(id) => {
                setRows((prev) =>
                  prev.filter((r) => Number(r.id) !== Number(id)),
                );
                setSelectedId("");
                setSearchParams({});
              }}
            />
          )}
        </Box>
      </Box>

      <SkillAgentTasksHelpDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />

      {/* Create task dialog */}
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>
          {t("skillAgent.dialog.newTaskTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <FormControl size="small" fullWidth sx={pageFieldSx}>
              <InputLabel>{t("skillAgent.form.clientFicha")}</InputLabel>
              <Select
                label={t("skillAgent.form.clientFicha")}
                value={newTask.SkillFichaId}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, SkillFichaId: e.target.value }))
                }
              >
                <MenuItem value="">{t("skillAgent.common.choose")}</MenuItem>
                {fichas.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.Titulo || f.SkillSlug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1.5}>
              <FormControl size="small" sx={{ flex: 1, ...pageFieldSx }}>
                <InputLabel>{t("skillAgent.form.taskType")}</InputLabel>
                <Select
                  label={t("skillAgent.form.taskType")}
                  value={newTask.TipoTarea}
                  onChange={(e) =>
                    setNewTask((p) => ({ ...p, TipoTarea: e.target.value }))
                  }
                >
                  {TASK_TYPE_KEYS.map((k) => (
                    <MenuItem key={k} value={k}>
                      {taskTypeLabel(k)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label={t("skillAgent.form.season")}
                placeholder={t("skillAgent.form.seasonPlaceholder")}
                value={newTask.Temporada}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, Temporada: e.target.value }))
                }
                sx={{ width: 120, ...pageFieldSx }}
              />
            </Stack>
            <TextField
              size="small"
              label={t("skillAgent.form.title")}
              placeholder={t("skillAgent.form.titlePlaceholder")}
              value={newTask.Titulo}
              onChange={(e) =>
                setNewTask((p) => ({ ...p, Titulo: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label={t("skillAgent.form.brief")}
              multiline
              minRows={3}
              value={newTask.Brief}
              onChange={(e) =>
                setNewTask((p) => ({ ...p, Brief: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
            <FormControl size="small" fullWidth sx={pageFieldSx}>
              <InputLabel>{t("skillAgent.form.useSearchOptional")}</InputLabel>
              <Select
                label={t("skillAgent.form.useSearchOptional")}
                value={newTask.searchId}
                onChange={(e) =>
                  setNewTask((p) => ({ ...p, searchId: e.target.value }))
                }
              >
                <MenuItem value="">
                  {t("skillAgent.form.noSearchRadarOnRun")}
                </MenuItem>
                {searchesForNewTask.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.Titulo}
                    {s.Temporada ? ` · ${s.Temporada}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={creating}
            onClick={handleCreate}
          >
            {creating
              ? t("skillAgent.common.creating")
              : t("skillAgent.common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create SEARCH dialog */}
      <Dialog
        open={createSearchOpen}
        onClose={() => !creatingSearch && setCreateSearchOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>
          {t("skillAgent.dialog.newSearchTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
              >
                {t("skillAgent.dialog.treeBranchLabel")}
              </Typography>
              <Typography variant="body1" fontWeight={800}>
                {[newSearch.CustomerName, newSearch.Temporada]
                  .filter(Boolean)
                  .join(" · ") || t("skillAgent.common.emDash")}
              </Typography>
            </Box>
            <TextField
              size="small"
              label={t("skillAgent.form.title")}
              placeholder={t("skillAgent.dialog.newSearchTitlePlaceholder")}
              value={newSearch.Titulo}
              onChange={(e) =>
                setNewSearch((p) => ({ ...p, Titulo: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label={t("skillAgent.form.tipologyKeywords")}
              value={newSearch.Tipologia}
              onChange={(e) =>
                setNewSearch((p) => ({ ...p, Tipologia: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
              helperText={t("skillAgent.form.tipologyHelper")}
              sx={pageFieldSx}
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                label={t("skillAgent.form.imgsPerBrand")}
                type="number"
                inputProps={{ min: 1, max: 12 }}
                value={newSearch.maxImagesPerBrand}
                onChange={(e) =>
                  setNewSearch((p) => ({
                    ...p,
                    maxImagesPerBrand: e.target.value,
                  }))
                }
                helperText={t("skillAgent.form.imgsPerBrandHelper")}
                sx={{ width: 120, ...pageFieldSx }}
              />
            </Stack>

            <SkillAgentBrandSourcesFields
              brands={searchBrands}
              brandIds={newSearch.brandIds}
              urlSelection={newSearch.urlSelection}
              onBrandIdsChange={(brandIds) =>
                setNewSearch((p) => ({ ...p, brandIds }))
              }
              onUrlSelectionChange={(urlSelection) =>
                setNewSearch((p) => ({ ...p, urlSelection }))
              }
              maxHeight={280}
            />

            <SkillAgentCustomSourcesFields
              sources={newSearch.customSources}
              onChange={(customSources) =>
                setNewSearch((p) => ({ ...p, customSources }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateSearchOpen(false)}
            disabled={creatingSearch}
          >
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={creatingSearch}
            onClick={handleCreateSearch}
          >
            {creatingSearch
              ? t("skillAgent.common.creating")
              : t("skillAgent.dialog.newSearchSubmit")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Moodboard dialog */}
      <Dialog
        open={createMoodboardOpen}
        onClose={() => !creatingMoodboard && setCreateMoodboardOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>
          {t("skillAgent.dialog.newMoodboardTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            {moodboardFormError ? (
              <Alert severity="error" onClose={() => setMoodboardFormError("")}>
                {moodboardFormError}
              </Alert>
            ) : null}
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
              >
                {t("skillAgent.dialog.treeBranchLabel")}
              </Typography>
              <Typography variant="body1" fontWeight={800}>
                {[newMoodboard.CustomerName, newMoodboard.Temporada]
                  .filter(Boolean)
                  .join(" · ") || t("skillAgent.common.emDash")}
              </Typography>
            </Box>
            <TextField
              size="small"
              label={t("skillAgent.form.title")}
              placeholder={t("skillAgent.dialog.newMoodboardTitlePlaceholder")}
              value={newMoodboard.Titulo}
              onChange={(e) =>
                setNewMoodboard((p) => ({ ...p, Titulo: e.target.value }))
              }
              required
              autoFocus
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label={t("skillAgent.form.categoryLabel")}
              placeholder={t("skillAgent.form.categoryPlaceholder")}
              value={newMoodboard.CategoryLabel}
              onChange={(e) =>
                setNewMoodboard((p) => ({
                  ...p,
                  CategoryLabel: e.target.value,
                }))
              }
              fullWidth
              sx={pageFieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateMoodboardOpen(false)}
            disabled={creatingMoodboard}
          >
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={creatingMoodboard}
            onClick={handleCreateMoodboard}
          >
            {creatingMoodboard
              ? t("skillAgent.common.creating")
              : t("skillAgent.dialog.newMoodboardSubmit")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
